import useSeo from "@/hooks/useSeo";
import { Shield, Mail } from "lucide-react";

export default function PrivacyPolicy() {
    useSeo({
        title: "Beeta Tracking App — Privacy Policy",
        description:
            "Privacy Policy for the Beeta Tracking mobile app by Beeta Logistics Private Limited. Learn what data we collect, how it's used, and your rights.",
        path: "/privacy-policy",
    });

    return (
        <main data-testid="privacy-policy-page" className="bg-white">
            {/* Hero */}
            <section className="relative bg-slate-900 text-white py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #3b82f6 0, transparent 40%), radial-gradient(circle at 80% 60%, #eab308 0, transparent 40%)" }} />
                <div className="relative max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-semibold uppercase tracking-wider mb-6">
                        <Shield className="w-3.5 h-3.5" />
                        Legal
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                        Beeta Tracking App
                        <span className="block text-blue-400 mt-2">Privacy Policy</span>
                    </h1>
                    <p className="mt-6 text-slate-300 text-base max-w-2xl leading-relaxed">
                        This Privacy Policy describes how Beeta Logistics Private Limited
                        (&ldquo;Beeta Logistics&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;)
                        collects, uses and safeguards your information when you use the
                        Beeta Tracking mobile application (the &ldquo;App&rdquo;).
                    </p>
                    <p className="mt-4 text-xs text-slate-500 uppercase tracking-widest">
                        Effective Date: February 1, 2026
                    </p>
                </div>
            </section>

            {/* Body */}
            <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 prose prose-slate max-w-none">
                <Section title="1. Introduction">
                    <p>
                        This Privacy Policy governs your use of the Beeta Tracking App on
                        a mobile device. By downloading, installing, registering with, or
                        using the App you agree to be bound by the terms of this Privacy
                        Policy. If you do not agree, please uninstall the App and
                        discontinue use.
                    </p>
                </Section>

                <Section title="2. What information the App collects and how it is used">
                    <h3>2.1 User-Provided Information</h3>
                    <p>
                        The App obtains information you provide when you download,
                        register and use the App. When you register with us and use the
                        App, you generally provide:
                    </p>
                    <ul>
                        <li>Your name, email address, age, username, password and other registration information.</li>
                        <li>Transaction-related information, such as when you make purchases, respond to offers, or download or use services from us.</li>
                        <li>Information you provide us when you contact us for help.</li>
                        <li>Payment / card information used for purchases or services within the App.</li>
                        <li>Information you enter into the App, such as contact and project management information.</li>
                    </ul>
                    <p>
                        We may use the information you provide to contact you from time to
                        time to provide important information, required notices, and
                        marketing promotions.
                    </p>

                    <h3>2.2 Automatically Collected Information</h3>
                    <p>
                        The App may automatically collect certain information, such as the
                        type of mobile device you use, your device&apos;s unique device
                        ID, IP address, mobile operating system, the type of mobile
                        internet browser you use, and information about how you use the
                        App.
                    </p>
                </Section>

                <Section title="3. Location information">
                    <p>
                        The Beeta Tracking App calculates transit charges based on the
                        distance covered by a vehicle using Google Maps. To provide this
                        service, we ask for permission to access your device&apos;s
                        precise real-time location so that accurate distance can be
                        measured.
                    </p>
                    <p>
                        We use your location information only to provide the requested
                        location-based services. You may at any time opt out from allowing
                        us to access your location data through your device settings or by
                        uninstalling the App. See the section &ldquo;Your opt-out
                        rights&rdquo; below.
                    </p>
                </Section>

                <Section title="4. Do third parties see or have access to information?">
                    <p>
                        We share your information with third parties only in the ways
                        described in this Privacy Policy. We may disclose User-Provided
                        and Automatically Collected Information:
                    </p>
                    <ul>
                        <li>As required by law, such as to comply with a subpoena or similar legal process;</li>
                        <li>When we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;</li>
                        <li>With our trusted service providers who work on our behalf, do not have an independent use of the information we disclose to them, and have agreed to adhere to the rules set forth in this Privacy Policy.</li>
                    </ul>
                    <p>
                        If Beeta Logistics is involved in a merger, acquisition or sale of
                        all or a portion of its assets, you will be notified via email
                        and/or a prominent notice on{" "}
                        <a href="https://www.beetalogistics.com/" className="text-blue-600 hover:underline">
                            https://www.beetalogistics.com/
                        </a>{" "}
                        of any change in ownership or uses of your information, as well as
                        any choices you may have.
                    </p>
                </Section>

                <Section title="5. Automatic data collection and advertising">
                    <p>
                        We may work with analytics companies to help us understand how the
                        App is being used, such as frequency and duration of usage. We may
                        also work with advertisers and third-party advertising networks
                        who need to know how you interact with advertising provided in the
                        App.
                    </p>
                    <p>
                        Advertisers and advertising networks may use certain information
                        collected by the App, including the unique device identifier and
                        mobile telephone number. To protect the anonymity of this
                        information, we use encryption technology to help ensure that
                        these third parties cannot identify you personally.
                    </p>
                    <p>
                        If you would like to opt out of third-party use of this
                        information to serve targeted advertising, please see &ldquo;Your
                        opt-out rights&rdquo; below.
                    </p>
                </Section>

                <Section title="6. Your opt-out rights">
                    <p>You have multiple opt-out options for use of the App:</p>
                    <ul>
                        <li>
                            <strong>Uninstall the App</strong> — you can stop all
                            collection of information by uninstalling the App using the
                            standard uninstall processes available on your mobile device
                            or through the app marketplace.
                        </li>
                        <li>
                            <strong>Opt out of targeted advertising</strong> — you may opt
                            out of the use of information to serve targeted advertising
                            through your device&apos;s advertising settings.
                        </li>
                        <li>
                            <strong>Location access</strong> — you may at any time revoke
                            location permission through your device settings.
                        </li>
                    </ul>
                </Section>

                <Section title="7. Data retention & managing your information">
                    <p>
                        We will retain your User-Provided data for as long as you use the
                        App and for a reasonable time thereafter. If you would like us to
                        delete User-Provided Data you have supplied via the App, please
                        contact us at{" "}
                        <a href="mailto:support@beetalogistics.com" className="text-blue-600 hover:underline">
                            support@beetalogistics.com
                        </a>{" "}
                        and we will respond in a reasonable time. Please note that some or
                        all of your User-Provided Data may be required for the App to
                        function properly, and we may be required to retain certain
                        information by law.
                    </p>
                </Section>

                <Section title="8. Children">
                    <p>
                        We do not use the App to knowingly solicit data from or market to
                        children under the age of 13. If a parent or guardian becomes
                        aware that his or her child has provided us with information
                        without their consent, he or she should contact us at{" "}
                        <a href="mailto:support@beetalogistics.com" className="text-blue-600 hover:underline">
                            support@beetalogistics.com
                        </a>
                        . We will delete such information from our files within a
                        reasonable time.
                    </p>
                </Section>

                <Section title="9. Security">
                    <p>
                        We are concerned about safeguarding the confidentiality of your
                        information. We provide physical, electronic and procedural
                        safeguards to protect information we process and maintain. For
                        example, we limit access to this information to authorized
                        employees and contractors who need to know that information in
                        order to operate, develop or improve our App. Please be aware
                        that, although we endeavour to provide reasonable security, no
                        security system can prevent all potential security breaches.
                    </p>
                </Section>

                <Section title="10. Changes to this Privacy Policy">
                    <p>
                        This Privacy Policy may be updated from time to time for any
                        reason. We will notify you of any changes to our Privacy Policy by
                        posting the new Privacy Policy on this page and, where
                        appropriate, informing you via email or in-app notice. You are
                        advised to consult this Privacy Policy regularly for any changes.
                    </p>
                </Section>

                <Section title="11. Your consent">
                    <p>
                        By using the App, you consent to our processing of User-Provided
                        and Automatically Collected information as set forth in this
                        Privacy Policy now and as amended by us.
                    </p>
                </Section>

                <Section title="12. Contact us">
                    <p>
                        If you have any questions regarding privacy while using the App,
                        or about our practices, please contact us:
                    </p>
                    <div className="not-prose mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center shrink-0">
                            <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">
                                Email Support
                            </div>
                            <a
                                href="mailto:support@beetalogistics.com"
                                data-testid="privacy-support-email"
                                className="text-lg font-semibold text-slate-900 hover:text-blue-600"
                            >
                                support@beetalogistics.com
                            </a>
                            <div className="text-sm text-slate-600 mt-2">
                                Beeta Logistics Private Limited · 1816, Tower-4, DLF
                                Corporate Greens, Sector 74-A, Gurgaon 122004, Haryana,
                                India
                            </div>
                        </div>
                    </div>
                </Section>
            </article>
        </main>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 mt-0">{title}</h2>
            <div className="text-slate-700 leading-relaxed space-y-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2">
                {children}
            </div>
        </section>
    );
}
